import React, { useState } from 'react';
import { 
  Shield, 
  Smartphone, 
  Key, 
  AlertTriangle, 
  ArrowLeft,
  Loader
} from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';

interface MFAVerificationProps {
  sessionToken: string;
  onSuccess: (tokens: { accessToken: string; refreshToken: string; user: any }) => void;
  onBack: () => void;
}

const MFAVerification: React.FC<MFAVerificationProps> = ({ 
  sessionToken, 
  onSuccess, 
  onBack 
}) => {
  const [verificationMethod, setVerificationMethod] = useState<'totp' | 'backup'>('totp');
  const [totpCode, setTotpCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleVerification = async () => {
    if (verificationMethod === 'totp' && (!totpCode || totpCode.length !== 6)) {
      setError('Veuillez entrer un code TOTP à 6 chiffres');
      return;
    }

    if (verificationMethod === 'backup' && (!backupCode || backupCode.length !== 8)) {
      setError('Veuillez entrer un code de récupération à 8 caractères');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const requestBody = {
        sessionToken,
        ...(verificationMethod === 'totp' 
          ? { totpCode } 
          : { backupCode: backupCode.toUpperCase() }
        )
      };

      const response = await fetch(API_ENDPOINTS.COMPLETE_MFA_LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      console.log('🔍 MFA response:', data);
      console.log('🔍 Response status:', response.status);

      if (data.success) {
        console.log('✅ MFA success, calling onSuccess with tokens');
        onSuccess({
          accessToken: data.data.accessToken,
          refreshToken: data.data.refreshToken,
          user: data.data.user
        });
      } else {
        console.error('❌ MFA failed:', data.error);
        setError(data.error || 'Erreur lors de la vérification');
      }
    } catch (error) {
      console.error('MFA verification error:', error);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleVerification();
    }
  };

  return (
    <div className="min-h-screen bg-osint-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="osint-card p-8 rounded-lg">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-osint-orange rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Authentification requise
            </h1>
            <p className="text-gray-300">
              Entrez votre code d'authentification pour continuer
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 flex items-center space-x-2 mb-6">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          {/* Method Selection */}
          <div className="mb-6">
            <div className="flex space-x-2 p-1 bg-osint-light-gray rounded-lg">
              <button
                onClick={() => setVerificationMethod('totp')}
                className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded text-sm transition-colors ${
                  verificationMethod === 'totp'
                    ? 'bg-osint-orange text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                <Smartphone className="w-4 h-4" />
                <span>Authenticator</span>
              </button>
              <button
                onClick={() => setVerificationMethod('backup')}
                className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded text-sm transition-colors ${
                  verificationMethod === 'backup'
                    ? 'bg-osint-orange text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                <Key className="w-4 h-4" />
                <span>Code de récupération</span>
              </button>
            </div>
          </div>

          {/* TOTP Input */}
          {verificationMethod === 'totp' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Code d'authentification (6 chiffres)
              </label>
              <input
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyPress={handleKeyPress}
                placeholder="123456"
                className="osint-input w-full text-center font-mono text-lg tracking-widest"
                maxLength={6}
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-2">
                Ouvrez votre application d'authentification (Google Authenticator, Authy, etc.) 
                et entrez le code à 6 chiffres.
              </p>
            </div>
          )}

          {/* Backup Code Input */}
          {verificationMethod === 'backup' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Code de récupération (8 caractères)
              </label>
              <input
                type="text"
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value.toUpperCase().slice(0, 8))}
                onKeyPress={handleKeyPress}
                placeholder="A1B2C3D4"
                className="osint-input w-full text-center font-mono text-lg tracking-widest"
                maxLength={8}
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-2">
                Utilisez l'un des codes de récupération que vous avez sauvegardés lors de l'activation du 2FA.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col space-y-3">
            <button
              onClick={handleVerification}
              disabled={
                loading || 
                (verificationMethod === 'totp' && totpCode.length !== 6) ||
                (verificationMethod === 'backup' && backupCode.length !== 8)
              }
              className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-osint-orange hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Vérification...</span>
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  <span>Vérifier</span>
                </>
              )}
            </button>

            <button
              onClick={onBack}
              disabled={loading}
              className="w-full flex items-center justify-center space-x-2 py-2 px-4 text-gray-300 hover:text-white disabled:opacity-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Retour à la connexion</span>
            </button>
          </div>

          {/* Help Text */}
          <div className="mt-6 pt-6 border-t border-osint-light-gray">
            <p className="text-xs text-gray-400 text-center">
              Vous avez perdu l'accès à votre téléphone ? Utilisez un code de récupération pour vous connecter.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFAVerification;